import { format } from "date-fns";
import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";

export type EventsPagePartner = {
  slug: string;
  name: string;
  website: string;
  logoUrl: string;
};

export type EventsPageType = {
  name: string;
  slug: string;
};

export type EventsPageSpeaker = {
  name: string;
  company: string;
  job: string;
  imageUrl: string;
};

export type AdditionalHubSpotFormField = {
  fieldName: string;
  fieldLabel: string;
  fieldType: "text" | "select" | "textarea";
  inputType?: string;
  selectOptions?: Array<{ label: string; value: string }>;
  required: boolean;
};

export type EventsPage = {
  slug: string;
  type?: string;
  title: string;
  showInDirectory: boolean;
  seriesSlug?: string;
  seriesCardTitle?: string;
  excerpt?: string;
  startDate: string;
  endDate?: string;
  time: string;
  location: string;
  description?: string;
  hubSpotFormHeader?: string;
  hubSpotFormId: string;
  allowPersonalEmails?: boolean;
  hubSpotFormDefaultFields?: string[];
  hubSpotFormAdditionalFields?: AdditionalHubSpotFormField[];
  customPrivacyPolicy?: string;
  sfdcCampaignId?: string;
  pastEventCtaText?: string;
  pastEventCtaLink?: string;
  formClosed?: boolean;
  formClosedCopy?: string;
  formSubmissionConfirmationCopy?: string;
  partners?: EventsPagePartner[];
  speakers?: EventsPageSpeaker[];
  about: string;
  imageUrl: string;
  address?: string;
  mapEmbedUrl?: string;
  metaTitle: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const getEventsPage = async (slug: string): Promise<EventsPage | undefined> => {
  const query = groq`
    *[_type == "eventsPage" && slug == $slug] {
      slug,
      'type': type->name,
      title,
      showInDirectory,
      'seriesSlug': series->slug,
      seriesCardTitle,
      excerpt,
      startDate,
      endDate,
      time,
      location,
      description,
      hubSpotFormHeader,
      hubSpotFormId,
      allowPersonalEmails,
      hubSpotFormDefaultFields,
      hubSpotFormAdditionalFields[]{ 
        required,
        fieldName, 
        fieldLabel, 
        fieldType, 
        inputType, 
        selectOptions[]{ 
          'label': selectLabel,
          'value': selectValue,
         },
      },
      customPrivacyPolicy,
      sfdcCampaignId,
      pastEventCtaText,
      pastEventCtaLink,
      formClosed,
      formClosedCopy,
      formSubmissionConfirmationCopy,
      partners[]->{ slug, name, website, 'logoUrl': logo.asset->url },
      speakers[]->{ name, company, job, 'imageUrl': image.asset->url },
      about,
      'imageUrl': image.asset->url,
      address,
      mapEmbedUrl,
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url,
    }
  `;

  try {
    const parameters = { slug };
    const eventsPage = await getClient().fetch(query, parameters);
    return eventsPage[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch events page with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};

export type EventsPageSimple = {
  _type: string;
  title: string;
  slug: string;
  showInDirectory: boolean;
  seriesSlug?: string;
  seriesCardTitle?: string;
  location: string;
  imageUrl: string;
  startDate: string;
  endDate?: string;
  excerpt: string;
  metaImageUrl?: string;
};

const eventsPageSimpleFragment =
  "_type, title, slug, showInDirectory, seriesCardTitle, location, startDate, endDate, excerpt, 'imageUrl': image.asset->url, 'metaImageUrl': metaImage.asset->url, 'seriesSlug': series->slug,";

export const getUpcomingEventsPages = async (): Promise<EventsPageSimple[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  // Exclude event pages with series - they are displayed on the series page instead.
  const query = groq`
    *[_type == "eventsPage" 
      && showInDirectory 
      && series == null 
      && (
        endDate == null && startDate >= $today 
        || endDate >= $today
      )
    ] {
      ${eventsPageSimpleFragment}
    } | order(startDate)
  `;

  try {
    const parameters = { today };
    const upcomingEventsPages = await getClient().fetch(query, parameters);
    return upcomingEventsPages;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch upcoming events pages: ${errorMessage}`);
    return [];
  }
};

export const getPastEventsPages = async (): Promise<EventsPageSimple[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  // Exclude event pages with series - they are displayed on the series page instead.
  const query = groq`
    *[_type == "eventsPage" 
      && showInDirectory 
      && series == null 
      && (
        endDate == null && startDate < $today 
        || endDate < $today
      )
    ] {
      ${eventsPageSimpleFragment}
    } | order(startDate desc)
  `;

  try {
    const parameters = { today };
    const pastEventsPages = await getClient().fetch(query, parameters);
    return pastEventsPages;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch past events pages: ${errorMessage}`);
    return [];
  }
};

export type EventSeries = {
  slug: string;
  title: string;
  showInDirectory: boolean;
  excerpt?: string;
  location: string;
  startDate: string;
  endDate?: string;
  imageUrl: string;
  description: string;
  metaTitle: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const eventSeriesFragment =
  "slug, title, showInDirectory, excerpt, location, startDate, endDate, description, metaTitle, metaDescription, 'imageUrl': image.asset->url, 'metaImageUrl': metaImage.asset->url";

export const getEventSeries = async (slug: string): Promise<EventSeries | undefined> => {
  const query = groq`
    *[_type == "eventSeries" && slug == $slug] {
     ${eventSeriesFragment}
    }
  `;

  try {
    const parameters = { slug };
    const eventSeries = await getClient().fetch(query, parameters);
    return eventSeries[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch event series with slug "${slug}": ${errorMessage}`);
    return undefined;
  }
};

export const getEventPagesWithSeries = async (seriesSlug: string): Promise<EventsPageSimple[]> => {
  const query = groq`
    *[_type == "eventsPage" && showInDirectory && series->slug == $seriesSlug] {
      ${eventsPageSimpleFragment}
    }
  `;

  try {
    const parameters = { seriesSlug };
    const eventPages = await getClient().fetch(query, parameters);
    return eventPages ?? [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch event pages with series slug "${seriesSlug}": ${errorMessage}`);
    return [];
  }
};

export type EventSeriesSimple = {
  _type: string;
  title: string;
  slug: string;
  showInDirectory: boolean;
  location: string;
  startDate: string;
  endDate?: string;
  excerpt?: string;
  imageUrl: string;
  metaImageUrl?: string;
};

const eventSeriesSimpleFragment =
  "_type, title, slug, showInDirectory, location, startDate, endDate, excerpt, 'imageUrl': image.asset->url, 'metaImageUrl': metaImage.asset->url";

export const getUpcomingEventSeries = async (): Promise<EventSeriesSimple[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  const query = groq`
    *[_type == "eventSeries" && showInDirectory && (endDate == null && startDate >= $today || endDate >= $today)] {
      ${eventSeriesSimpleFragment}
    } | order(startDate)
  `;

  try {
    const parameters = { today };
    const upcomingEventSeries = await getClient().fetch(query, parameters);
    return upcomingEventSeries;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch upcoming event series: ${errorMessage}`);
    return [];
  }
};

export const getPastEventSeries = async (): Promise<EventSeriesSimple[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  const query = groq`
    *[_type == "eventSeries" && showInDirectory && (endDate == null && startDate < $today || endDate < $today)] {
      ${eventSeriesSimpleFragment}
    } | order(startDate desc)
  `;

  try {
    const parameters = { today };
    const pastEventSeries = await getClient().fetch(query, parameters);
    return pastEventSeries;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch past event series: ${errorMessage}`);
    return [];
  }
};

export type ExternalEvent = {
  _type: string;
  title: string;
  location: string;
  excerpt: string;
  imageUrl: string;
  startDate: string;
  endDate?: string;
  registrationUrl: string;
};

const externalEventFragment =
  "_type, title, location, 'excerpt': description, startDate, endDate, registrationUrl, 'imageUrl': image.asset->url";

export const getUpcomingExternalEvents = async (): Promise<ExternalEvent[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  const query = groq`
    *[_type == "event" && (endDate == null && startDate >= $today || endDate >= $today)] {
      ${externalEventFragment}
    } | order(startDate)
  `;

  try {
    const parameters = { today };
    const upcomingExternalEvents = await getClient().fetch(query, parameters);
    return upcomingExternalEvents;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch upcoming external events: ${errorMessage}`);
    return [];
  }
};

export const getPastExternalEvents = async (): Promise<ExternalEvent[]> => {
  const date = new Date();
  const today = format(date, "yyyy-MM-dd");

  const query = groq`
    *[_type == "event" && (endDate == null && startDate < $today || endDate < $today)] {
      ${externalEventFragment}
    } | order(startDate desc)
  `;

  try {
    const parameters = { today };
    const pastExternalEvents = await getClient().fetch(query, parameters);
    return pastExternalEvents;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch past external events: ${errorMessage}`);
    return [];
  }
};
